// AI Local Ranker - Fast client-side filtering with intelligent scoring
export interface LocalRankerResult {
  results: Array<{
    item: any;
    reason: string;
    relevanceScore: number;
  }>;
  appliedFilters: Record<string, any>;
  intent: string;
  totalFound: number;
}

// Enhanced keyword mappings for semantic search with synonyms
const CATEGORY_KEYWORDS = {
  'Design': ['design', 'ui', 'ux', 'interface', 'visual', 'graphic', 'layout', 'aesthetic', 'styling', 'appearance', 'look', 'feel', 'brand', 'identity', 'creative', 'art', 'composition', 'typography', 'color', 'themes', 'beautiful', 'pretty', 'image', 'images', 'photo', 'photos', 'picture', 'pictures', 'icon', 'icons', 'illustration', 'illustrations', 'drawing', 'drawings', 'artwork', 'visual design', 'graphic design', 'digital art', 'vector', 'vectors', 'raster', 'bitmap', 'png', 'jpg', 'svg', 'pdf', 'print', 'logo', 'logos'],
  'Productivity': ['productivity', 'note', 'notes', 'note taking', 'note-taking', 'notebook', 'notepad', 'journal', 'journaling', 'diary', 'memo', 'memos', 'task', 'tasks', 'todo', 'to-do', 'planning', 'organization', 'organize', 'knowledge management', 'personal knowledge', 'second brain', 'zettelkasten', 'markdown', 'writing', 'text editor', 'documentation'],
  'Prototyping': ['prototype', 'wireframe', 'mockup', 'interactive', 'demo', 'preview', 'sketch', 'draft', 'concept', 'proof of concept', 'mvp', 'minimum viable', 'early stage', 'rough', 'quick', 'rapid', 'iteration', 'test idea'],
  'Research': ['research', 'testing', 'user test', 'analytics', 'feedback', 'survey', 'interview', 'analysis', 'insights', 'data', 'metrics', 'stats', 'study', 'investigation', 'discovery', 'understanding', 'behavior', 'psychology', 'usability study', 'user experience research'],
  'Collaboration': ['collaboration', 'team', 'share', 'comment', 'review', 'workflow', 'teamwork', 'group work', 'cooperative', 'together', 'joint', 'collective', 'communication', 'coordination', 'sync', 'shared', 'remote work', 'distributed'],
  'Animation': ['animation', 'motion', 'transition', 'micro-interaction', 'lottie', 'gif', 'movement', 'animate', 'dynamic', 'interactive', 'smooth', 'effects', 'kinetic', 'fluid', 'engaging', 'delightful', 'fun', 'playful'],
  'Development': ['code', 'developer', 'html', 'css', 'react', 'component', 'handoff', 'programming', 'coding', 'build', 'implementation', 'technical', 'engineering', 'frontend', 'backend', 'full stack', 'web dev', 'software'],
  'Testing': ['test', 'usability', 'a/b test', 'user test', 'feedback', 'validation', 'quality assurance', 'qa', 'verification', 'evaluation', 'assessment', 'performance', 'bug', 'issue', 'debugging', 'reliability']
};

const USE_CASE_KEYWORDS = {
  'UI design': ['ui', 'interface', 'screen', 'app design', 'web design', 'digital design', 'mobile design'],
  'Wireframing': ['wireframe', 'sketch', 'layout', 'structure', 'blueprint'],
  'Design systems': ['design system', 'component', 'library', 'style guide', 'token'],
  'Usability testing': ['usability', 'user test', 'test', 'feedback', 'validation'],
  'Collaboration': ['collaborate', 'team', 'share', 'review', 'comment'],
  'Prototyping': ['prototype', 'interactive', 'clickable', 'demo'],
  'User research': ['research', 'survey', 'interview', 'persona', 'journey'],
  'Microinteractions': ['micro', 'animation', 'hover', 'transition', 'interaction'],
  'Image editing': ['image', 'images', 'photo', 'photos', 'picture', 'pictures', 'edit', 'editing', 'retouch', 'enhance', 'filter', 'crop', 'resize', 'compress', 'optimize'],
  'Icon design': ['icon', 'icons', 'symbol', 'symbols', 'pictogram', 'glyph', 'iconography'],
  'Illustration': ['illustration', 'illustrations', 'drawing', 'drawings', 'artwork', 'digital art', 'vector art'],
  'Note taking': ['note', 'notes', 'note taking', 'note-taking', 'notebook', 'notepad', 'journal', 'journaling', 'diary', 'memo', 'memos', 'writing', 'text editor', 'documentation', 'knowledge management', 'personal knowledge', 'second brain', 'zettelkasten', 'markdown', 'org-mode']
};

const CONTENT_TYPE_KEYWORDS = {
  'Article': ['article', 'blog', 'post', 'read', 'guide'],
  'Tutorial': ['tutorial', 'how-to', 'guide', 'step by step', 'learn'],
  'Template': ['template', 'starter', 'boilerplate', 'example'],
  'Checklist': ['checklist', 'list', 'steps', 'process'],
  'Tool': ['tool', 'app', 'software', 'platform'],
  'Course': ['course', 'class', 'training', 'education'],
  'Video': ['video', 'watch', 'tutorial', 'demo'],
  'Book': ['book', 'ebook', 'read', 'chapter'],
  'Podcast': ['podcast', 'listen', 'audio', 'episode']
};

const PRICING_KEYWORDS = {
  'Free': ['free', 'no cost', 'gratis', 'zero cost', 'open source'],
  'Freemium': ['freemium', 'free tier', 'free plan', 'limited free', 'trial'],
  'Paid': ['paid', 'premium', 'subscription', 'pro', 'enterprise', 'commercial']
};

const PLATFORM_KEYWORDS = {
  'Web': ['web', 'browser', 'online', 'cloud', 'saas'],
  'macOS': ['mac', 'macos', 'apple', 'osx'],
  'Windows': ['windows', 'pc', 'win'],
  'iOS': ['ios', 'iphone', 'ipad', 'mobile'],
  'Android': ['android', 'mobile', 'phone'],
  'Plugins': ['plugin', 'extension', 'add-on', 'figma plugin', 'sketch plugin']
};

const AI_KEYWORDS = ['ai', 'artificial intelligence', 'machine learning', 'automated', 'smart', 'intelligent', 'gpt', 'openai'];

function extractFiltersFromQuery(query: string) {
  const lowerQuery = query.toLowerCase();
  const extractedFilters: any = {
    categories: [],
    useCases: [],
    contentTypes: [],
    pricing: [],
    platforms: [],
    aiEnabled: null
  };

  // Extract categories
  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    if (keywords.some(keyword => lowerQuery.includes(keyword))) {
      extractedFilters.categories.push(category);
    }
  }

  // Extract use cases
  for (const [useCase, keywords] of Object.entries(USE_CASE_KEYWORDS)) {
    if (keywords.some(keyword => lowerQuery.includes(keyword))) {
      extractedFilters.useCases.push(useCase);
    }
  }

  // Extract content types (for resources)
  for (const [contentType, keywords] of Object.entries(CONTENT_TYPE_KEYWORDS)) {
    if (keywords.some(keyword => lowerQuery.includes(keyword))) {
      extractedFilters.contentTypes.push(contentType);
    }
  }

  // Extract pricing
  for (const [pricing, keywords] of Object.entries(PRICING_KEYWORDS)) {
    if (keywords.some(keyword => lowerQuery.includes(keyword))) {
      extractedFilters.pricing.push(pricing);
    }
  }

  // Extract platforms
  for (const [platform, keywords] of Object.entries(PLATFORM_KEYWORDS)) {
    if (keywords.some(keyword => lowerQuery.includes(keyword))) {
      extractedFilters.platforms.push(platform);
    }
  }

  // Check for AI preference
  if (AI_KEYWORDS.some(keyword => lowerQuery.includes(keyword))) {
    extractedFilters.aiEnabled = true;
  }

  return extractedFilters;
}

function scoreRelevance(item: any, query: string, filters: any): number {
  const lowerQuery = query.toLowerCase();
  const queryWords = lowerQuery.split(/\s+/).filter(word => word.length > 1); // Allow 2+ char words
  let baseScore = 0;

  // Handle both tools and resources
  const name = item.name || item['Resource Name'] || '';
  const summary = item.summary || item['Description'] || '';
  const contentType = item['Content Type'] || '';
  const topicTags = item['Topic Tags'] || '';
  const useCase = item['Recommended Use Case'] || '';

  // Enhanced semantic matching - check for synonyms and related terms
  const semanticMatches = () => {
    let semanticScore = 0;
    
    // Check all keyword categories for semantic matches
    for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
      if (keywords.some(keyword => lowerQuery.includes(keyword))) {
        // If item belongs to this category, boost score significantly
        if (item.category && item.category.includes(category)) {
          semanticScore += 25; // Increased from 15
        }
        // Semantic relevance even if not exact category match
        if (name && keywords.some(keyword => name.toLowerCase().includes(keyword))) {
          semanticScore += 15; // Increased from 8
        }
        if (summary && keywords.some(keyword => summary.toLowerCase().includes(keyword))) {
          semanticScore += 12; // Increased from 6
        }
      }
    }
    
    // Check use case keywords too
    for (const [useCase, keywords] of Object.entries(USE_CASE_KEYWORDS)) {
      if (keywords.some(keyword => lowerQuery.includes(keyword))) {
        if (item.use_cases && item.use_cases.includes(useCase)) {
          semanticScore += 20;
        }
        if (name && keywords.some(keyword => name.toLowerCase().includes(keyword))) {
          semanticScore += 12;
        }
        if (summary && keywords.some(keyword => summary.toLowerCase().includes(keyword))) {
          semanticScore += 8;
        }
      }
    }
    
    return semanticScore;
  };

  // Enhanced name matching with partial matches and word boundaries
  if (name) {
    const nameLower = name.toLowerCase();
    
    // Exact match gets highest score
    if (nameLower === lowerQuery) {
      baseScore += 40;
    }
    // Full query contained in name
    else if (nameLower.includes(lowerQuery)) {
      baseScore += 30;
    }
    // Individual word matches in name
    else {
      const nameWords = nameLower.split(/\s+/);
      const wordMatches = queryWords.filter(queryWord => 
        nameWords.some(nameWord => nameWord.includes(queryWord) || queryWord.includes(nameWord))
      ).length;
      baseScore += wordMatches * 8;
    }
  }

  // Enhanced summary/description matching
  if (summary) {
    const summaryLower = summary.toLowerCase();
    
    // Full query match
    if (summaryLower.includes(lowerQuery)) {
      baseScore += 20;
    }
    // Individual word matches
    const wordMatches = queryWords.filter(word => summaryLower.includes(word)).length;
    baseScore += wordMatches * 4;
  }

  // Enhanced content type matching for resources
  if (contentType) {
    const contentTypeLower = contentType.toLowerCase();
    if (contentTypeLower.includes(lowerQuery)) {
      baseScore += 15;
    }
    const wordMatches = queryWords.filter(word => contentTypeLower.includes(word)).length;
    baseScore += wordMatches * 6;
  }

  // Enhanced topic tags matching for resources
  if (topicTags) {
    const topicTagsLower = topicTags.toLowerCase();
    if (topicTagsLower.includes(lowerQuery)) {
      baseScore += 12;
    }
    const wordMatches = queryWords.filter(word => topicTagsLower.includes(word)).length;
    baseScore += wordMatches * 4;
  }

  // Enhanced use case matching for resources
  if (useCase) {
    const useCaseLower = useCase.toLowerCase();
    if (useCaseLower.includes(lowerQuery)) {
      baseScore += 10;
    }
    const wordMatches = queryWords.filter(word => useCaseLower.includes(word)).length;
    baseScore += wordMatches * 3;
  }

  // Category matches (for tools) with enhanced scoring
  if (item.category && filters.categories.length > 0) {
    const matches = filters.categories.filter((cat: string) => 
      item.category.includes(cat)
    ).length;
    baseScore += matches * 8;
  }

  // Use case matches (for tools) with enhanced scoring
  if (item.use_cases && filters.useCases.length > 0) {
    const matches = filters.useCases.filter((useCase: string) => 
      item.use_cases.includes(useCase)
    ).length;
    baseScore += matches * 6;
  }

  // Pricing match (for tools)
  if (item.pricing && filters.pricing.includes(item.pricing)) {
    baseScore += 8;
  }

  // Platform matches (for tools)
  if (item.platforms && filters.platforms.length > 0) {
    const matches = filters.platforms.filter((platform: string) => 
      item.platforms.includes(platform)
    ).length;
    baseScore += matches * 5;
  }

  // AI preference match (for tools)
  if (filters.aiEnabled !== null && item.ai_enabled === filters.aiEnabled) {
    baseScore += 10;
  }

  // Enhanced tag matches (for tools)
  if (item.tags) {
    const exactTagMatches = item.tags.filter((tag: string) => 
      lowerQuery.includes(tag.toLowerCase()) || tag.toLowerCase().includes(lowerQuery)
    ).length;
    baseScore += exactTagMatches * 5;
    
    const wordTagMatches = item.tags.filter((tag: string) => 
      queryWords.some(word => tag.toLowerCase().includes(word))
    ).length;
    baseScore += wordTagMatches * 3;
  }

  // Add semantic matching score
  baseScore += semanticMatches();

  // Quality and popularity bonuses (convert to percentage)
  let qualityScore = 0;
  
  // Popularity bonus for tools (0-10 points)
  if (item.popularity) {
    qualityScore += Math.min(item.popularity / 10, 10); // Max 10 points for popularity
  }
  
  // AI-enabled bonus (5 points if relevant to query)
  if (item.ai_enabled && (lowerQuery.includes('ai') || lowerQuery.includes('smart') || lowerQuery.includes('automated'))) {
    qualityScore += 5;
  }
  
  // Logo quality bonus (2 points for having logo)
  if (item.logo) {
    qualityScore += 2;
  }
  
  // Free tool bonus (3 points)
  if (item.pricing === 'Free') {
    qualityScore += 3;
  }

  // Calculate final percentage score (0-100%) - More precise scoring
  const totalScore = baseScore + qualityScore;
  const percentage = Math.min(Math.round(totalScore * 1.2), 100); // More precise scoring
  
  return Math.max(percentage, 0);
}

function generateReason(item: any, query: string, score: number): string {
  const lowerQuery = query.toLowerCase();
  const queryWords = lowerQuery.split(/\s+/).filter(word => word.length > 2);
  const extractedFilters = extractFiltersFromQuery(query);
  
  // Handle both tools and resources
  const name = item.name || item['Resource Name'] || '';
  const summary = item.summary || item['Description'] || '';
  const contentType = item['Content Type'] || '';
  const topicTags = item['Topic Tags'] || '';
  const useCase = item['Recommended Use Case'] || '';
  
  // Priority 1: Perfect match (90%+ relevance)
  if (score >= 90 && name && name.toLowerCase() === lowerQuery) {
    const context = item.category && item.category.length > 0 
      ? ` Perfect for ${item.category[0].toLowerCase()} workflows` 
      : contentType ? ` This ${contentType.toLowerCase()} resource` : '';
    const bonus = item.ai_enabled ? ' Features cutting-edge AI automation' : '';
    const quality = item.popularity && item.popularity > 80 ? ' Highly popular in the community' : '';
    return `🎯 Exact match found!${context}.${bonus}${quality} This is precisely what you're looking for.`;
  }

  // Priority 2: Exceptional relevance (80-89%)
  if (score >= 80) {
    const context = item.category && item.category.length > 0 
      ? ` leading ${item.category[0].toLowerCase()} solution` 
      : contentType ? ` comprehensive ${contentType.toLowerCase()}` : ' top-rated option';
    const ai = item.ai_enabled ? ' with intelligent AI features' : '';
    const pricing = item.pricing === 'Free' ? ' Available at no cost' : '';
    return `⭐ Outstanding match - this${context}${ai} aligns perfectly with your needs.${pricing} Highly recommended for your use case.`;
  }

  // Priority 3: Strong semantic match (70-79%)
  if (score >= 70) {
    // Check for semantic category matches
    for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
      if (keywords.some(keyword => lowerQuery.includes(keyword)) && 
          item.category && item.category.includes(category)) {
        const ai = item.ai_enabled ? ' Enhanced with smart AI capabilities' : '';
        const popularity = item.popularity && item.popularity > 70 ? ' Trusted by many users' : '';
        return `🚀 Excellent semantic match for ${category.toLowerCase()}.${ai}${popularity} This tool excels in exactly what you're seeking.`;
      }
    }
    
    const context = contentType ? ` ${contentType.toLowerCase()} resource` : ' solution';
    const topics = topicTags ? ` covering ${topicTags.split(',')[0].trim().toLowerCase()}` : '';
    return `💡 Strong relevance - this${context}${topics} addresses your requirements effectively. Great choice for your project.`;
  }

  // Priority 4: Good match with quality factors (60-69%)
  if (score >= 60) {
    const quality = item.popularity && item.popularity > 60 ? 'Popular choice' : 'Solid option';
    const context = item.category && item.category.length > 0 
      ? ` for ${item.category[0].toLowerCase()}` 
      : contentType ? ` ${contentType.toLowerCase()} resource` : '';
    const ai = item.ai_enabled ? ' Features modern AI enhancements' : '';
    return `✨ ${quality}${context}.${ai} Well-suited for your needs with good feature alignment.`;
  }

  // Priority 5: Content type match (for resources) (50-59%)
  if (contentType && (contentType.toLowerCase().includes(lowerQuery) || 
      queryWords.some(word => contentType.toLowerCase().includes(word)))) {
    const topics = topicTags ? ` focusing on ${topicTags.split(',')[0].trim().toLowerCase()}` : '';
    const use = useCase ? ` Ideal for ${useCase.split(',')[0].trim().toLowerCase()}` : '';
    return `📚 Relevant ${contentType.toLowerCase()} resource${topics}.${use} Good match for learning and reference.`;
  }

  // Priority 6: Use case match (for resources) (40-59%)
  if (useCase && (useCase.toLowerCase().includes(lowerQuery) ||
      queryWords.some(word => useCase.toLowerCase().includes(word)))) {
    const content = contentType ? ` ${contentType.toLowerCase()}` : ' resource';
    const topics = topicTags ? ` covering ${topicTags.split(',')[0].trim().toLowerCase()}` : '';
    return `🎯 Perfect use case alignment.${content}${topics} designed for exactly your situation. Highly practical for your needs.`;
  }

  // Priority 7: AI-powered emphasis (40-59%)
  if (extractedFilters.aiEnabled && item.ai_enabled && score >= 40) {
    const category = item.category && item.category.length > 0 
      ? ` ${item.category[0].toLowerCase()}` 
      : '';
    const useCase = item.use_cases && item.use_cases.length > 0 
      ? ` for ${item.use_cases[0].toLowerCase()}` 
      : '';
    return `🤖 AI-powered${category} solution${useCase}. Leverages artificial intelligence to automate workflows and enhance productivity.`;
  }

  // Priority 8: Platform/pricing preference match (30-49%)
  if (score >= 30) {
    const platform = item.platforms && item.platforms.length > 0 ? ` Works on ${item.platforms[0]}` : '';
    const pricing = item.pricing === 'Free' ? ' Free to use' : item.pricing === 'Freemium' ? ' Freemium model available' : '';
    const category = item.category && item.category.length > 0 ? ` ${item.category[0].toLowerCase()}` : '';
    return `⚡ Good${category} match with practical benefits.${platform}.${pricing} Solid choice for your requirements.`;
  }

  // Priority 9: Topic relevance (for resources) (20-39%)
  if (topicTags && score >= 20) {
    const topics = topicTags.split(',').map(t => t.trim().toLowerCase());
    const matchingTopics = topics.filter(topic => 
      queryWords.some(word => topic.includes(word))
    );
    
    if (matchingTopics.length > 0) {
      const content = contentType ? ` ${contentType.toLowerCase()}` : ' resource';
      const use = useCase ? ` for ${useCase.split(',')[0].trim().toLowerCase()}` : '';
      return `📖 Covers ${matchingTopics[0]} and related concepts.${content}${use} relevant to your learning goals.`;
    }
  }

  // Fallback with score-based reasoning
  if (score >= 40) {
    const category = item.category && item.category.length > 0 
      ? ` ${item.category[0].toLowerCase()}` 
      : contentType ? ` ${contentType.toLowerCase()}` : '';
    const features = item.ai_enabled ? ' AI-enhanced' : ' professional';
    return `🔍 Relevant${category}${features} solution with good potential for your use case. Worth exploring further.`;
  } else if (score >= 20) {
    const category = item.category && item.category.length > 0 
      ? ` ${item.category[0].toLowerCase()}` 
      : contentType ? ` ${contentType.toLowerCase()}` : '';
    return `💭 Potential${category} match that may address some aspects of your search. Consider for broader exploration.`;
  } else {
    const category = item.category && item.category.length > 0 
      ? ` ${item.category[0].toLowerCase()}` 
      : contentType ? ` ${contentType.toLowerCase()}` : '';
    return `🔄 Alternative${category} option that might offer different approaches to your requirements.`;
  }
}

function generateIntent(query: string, extractedFilters: any): string {
  const lowerQuery = query.toLowerCase();
  const queryWords = lowerQuery.split(/\s+/).filter(word => word.length > 2);
  
  // Enhanced intent detection with more context and personalization
  
  // AI-powered solutions intent
  if (extractedFilters.aiEnabled) {
    const category = extractedFilters.categories.length > 0 ? extractedFilters.categories[0].toLowerCase() : 'productivity';
    const context = queryWords.some(word => ['automate', 'smart', 'intelligent'].includes(word))
      ? 'to automate workflows and enhance efficiency'
      : queryWords.some(word => ['creative', 'design', 'generate'].includes(word))
        ? 'to boost creativity and generate innovative solutions'
        : 'to leverage artificial intelligence for better results';
    return `Seeking cutting-edge AI-powered ${category} tools ${context}`;
  }
  
  // Learning and skill development intent
  if (queryWords.some(word => ['learn', 'tutorial', 'guide', 'how', 'course', 'training', 'education', 'beginner'].includes(word))) {
    const skillLevel = queryWords.some(word => ['beginner', 'start', 'new', 'basic'].includes(word)) 
      ? 'beginner-friendly' 
      : queryWords.some(word => ['advanced', 'expert', 'pro'].includes(word))
        ? 'advanced'
        : 'comprehensive';
    const topics = extractedFilters.contentTypes.length > 0 
      ? extractedFilters.contentTypes.join(', ').toLowerCase()
      : 'educational';
    return `Looking for ${skillLevel} ${topics} resources to develop expertise and practical skills`;
  }
  
  // Category-specific professional intent
  if (extractedFilters.categories.length > 0) {
    const categories = extractedFilters.categories.join(' and ').toLowerCase();
    const useCase = extractedFilters.useCases.length > 0 ? ` specialized for ${extractedFilters.useCases[0].toLowerCase()}` : '';
    const teamContext = queryWords.some(word => ['team', 'collaborative', 'remote'].includes(word))
      ? ' for team collaboration and workflow optimization'
      : queryWords.some(word => ['personal', 'freelance', 'solo'].includes(word))
        ? ' for individual productivity and efficiency'
        : ' to enhance professional workflow';
    return `Seeking ${categories} solutions${useCase}${teamContext}`;
  }
  
  // Content type specific learning intent
  if (extractedFilters.contentTypes.length > 0) {
    const contentTypes = extractedFilters.contentTypes.join(' and ').toLowerCase();
    const purpose = queryWords.some(word => ['reference', 'quick', 'lookup'].includes(word))
      ? 'for quick reference and practical application'
      : queryWords.some(word => ['deep', 'comprehensive', 'master'].includes(word))
        ? 'for deep understanding and mastery'
        : 'to learn and improve professional skills';
    return `Seeking high-quality ${contentTypes} resources ${purpose}`;
  }
  
  // Budget-conscious intent
  if (extractedFilters.pricing.includes('Free')) {
    const quality = queryWords.some(word => ['best', 'top', 'quality', 'professional'].includes(word))
      ? 'high-quality free solutions that deliver professional results'
      : 'free solutions and resources to achieve goals without budget constraints';
    return `Searching for ${quality}`;
  }
  
  // Platform-specific intent
  if (extractedFilters.platforms.length > 0) {
    const platforms = extractedFilters.platforms.join(' and ');
    return `Looking for ${platforms}-optimized solutions that integrate seamlessly with current setup and workflows`;
  }
  
  // Problem-solving intent
  if (queryWords.some(word => ['solve', 'fix', 'problem', 'issue', 'challenge', 'improve', 'better'].includes(word))) {
    return `Seeking targeted solutions to overcome specific challenges and improve current processes`;
  }
  
  // Research and comparison intent
  if (queryWords.some(word => ['compare', 'vs', 'versus', 'alternative', 'options', 'best', 'top'].includes(word))) {
    return `Researching and comparing options to make informed decisions based on specific requirements`;
  }
  
  // Project phase specific intent
  if (queryWords.some(word => ['prototype', 'wireframe', 'mockup', 'concept', 'early'].includes(word))) {
    return `Working in early design phases, focusing on rapid prototyping and concept development`;
  }
  
  if (queryWords.some(word => ['test', 'testing', 'feedback', 'user', 'validation'].includes(word))) {
    return `In testing and validation phase, seeking tools for user research and feedback collection`;
  }
  
  // General tool-seeking intent
  if (queryWords.some(word => ['tool', 'app', 'software', 'platform', 'use', 'need'].includes(word))) {
    const workStyle = queryWords.some(word => ['remote', 'distributed', 'online'].includes(word))
      ? 'for remote work and distributed teams'
      : queryWords.some(word => ['mobile', 'on-the-go', 'anywhere'].includes(word))
        ? 'for mobile and flexible work environments'
        : 'to enhance productivity and streamline workflows';
    return `Searching for practical tools ${workStyle}`;
  }
  
  // Fallback with enhanced context
  const hasSpecificFilters = Object.values(extractedFilters).some((value: any) => 
    Array.isArray(value) ? value.length > 0 : value !== null
  );
  
  if (hasSpecificFilters) {
    return `Exploring specialized solutions that match specific requirements and integrate well with current workflow`;
  }
  
  return `Seeking comprehensive solutions related to "${query}" to find the most suitable options for specific goals and requirements`;
}

export async function runLocalAIRanker(
  query: string, 
  type: 'tools' | 'resources',
  data: any[]
): Promise<LocalRankerResult> {
  console.log(`🔍 Local AI Ranker processing: "${query}" for ${type}`);
  
  const extractedFilters = extractFiltersFromQuery(query);
  const intent = generateIntent(query, extractedFilters);
  
  // Score and filter items with dynamic relevance threshold
  const allScoredItems = data
    .map(item => ({
      item,
      relevanceScore: scoreRelevance(item, query, extractedFilters),
      reason: ''
    }))
    .sort((a, b) => b.relevanceScore - a.relevanceScore);

  // Higher threshold for more precise results
  const minRelevanceThreshold = 40; // Increased from 15 to 40
  const minResultsToShow = 3; // Reduced from 5 to 3
  const maxResultsToShow = 15; // Reduced from 50 to 15
  
  const relevantItems = allScoredItems.filter(item => item.relevanceScore >= minRelevanceThreshold);
  const finalItems = relevantItems.length >= minResultsToShow 
    ? relevantItems.slice(0, maxResultsToShow)
    : allScoredItems.slice(0, Math.max(minResultsToShow, relevantItems.length));

  const scoredItems = finalItems.map(item => ({
    ...item,
    reason: generateReason(item.item, query, item.relevanceScore)
  }));

  console.log(`✨ Local AI Ranker found ${scoredItems.length} results with scores: ${scoredItems.slice(0, 5).map(r => r.relevanceScore).join(', ')}`);

  return {
    results: scoredItems,
    appliedFilters: extractedFilters,
    intent,
    totalFound: scoredItems.length
  };
}

// Cache for performance
const queryCache = new Map<string, { result: LocalRankerResult; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export async function runCachedLocalAIRanker(
  query: string,
  type: 'tools' | 'resources', 
  data: any[]
): Promise<LocalRankerResult> {
  const cacheKey = `${type}:${query}`;
  const cached = queryCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log('📋 Using cached AI results');
    return cached.result;
  }
  
  const result = await runLocalAIRanker(query, type, data);
  queryCache.set(cacheKey, { result, timestamp: Date.now() });
  
  return result;
}